import { useRouter } from 'found'
import type { TouchEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedSelector } from '../../index'
import { buildPunctualityPath, resolveIntervalFromQuery } from '../../lib/wagonQueryParams'
import { getCurrentTrain, getCurrentTrainParts } from '../../reducers/wagonSlice'
import LoadingIndicator from '../LoadingIndicator'
import BottomNavbar from './BottomNavbar'
import StationInterval from './StationInterval'
import WagonRows from './WagonRows'

const WagonSection = () => {
  const { t } = useTranslation()
  const { router } = useRouter()
  const trainData = useTypedSelector(getCurrentTrain)
  const trainParts = useTypedSelector(getCurrentTrainParts)
  const trainNumber = useTypedSelector((state) => state.punctuality.selectedTrainNumber)
  const trainDate = useTypedSelector((state) => state.punctuality.selectedTrainDate)
  const search = useTypedSelector((state) => state.found.match.location.search)
  const locationState = useTypedSelector((state) => state.found.match.location.state)
  const resolvedInterval = resolveIntervalFromQuery(search, trainParts)
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const intervalKey = resolvedInterval !== null ? `${resolvedInterval}` : 'none'
  const previousIntervalKey = useRef(intervalKey)
  const pendingSwipe = useRef<{
    direction: 'next' | 'previous'
    nextPartIndex: number
  } | null>(null)
  const [translateX, setTranslateX] = useState('0%')
  const [transitionEnabled, setTransitionEnabled] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)
  const slideDurationMs = 120

  const getNextPartIndex = (direction: 'next' | 'previous') => {
    if (!trainParts?.length || resolvedInterval === null) return null

    const nextIndex = direction === 'next' ? resolvedInterval + 1 : resolvedInterval - 1
    if (nextIndex < 0 || nextIndex >= trainParts.length) return null

    return nextIndex
  }

  const navigateToPartIndex = (nextIndex: number) => {
    if (!trainParts || !trainParts[nextIndex]) return

    const nextPath = buildPunctualityPath(trainDate, trainNumber, {
      index: nextIndex,
    })
    const nextUrl = new URL(nextPath, window.location.origin)
    router.replace({
      pathname: nextUrl.pathname,
      search: nextUrl.search,
      state: locationState,
    })
  }

  const startSwipeAnimation = (direction: 'next' | 'previous') => {
    if (isAnimating) return

    const nextIndex = getNextPartIndex(direction)
    if (nextIndex === null) return

    pendingSwipe.current = { direction, nextPartIndex: nextIndex }
    setIsAnimating(true)
    setTransitionEnabled(true)
    setTranslateX(direction === 'next' ? '-100%' : '100%')

    window.setTimeout(() => {
      navigateToPartIndex(nextIndex)
    }, slideDurationMs)
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    touchStartX.current = touch.clientX
    touchStartY.current = touch.clientY
  }

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) return

    const touch = event.changedTouches[0]
    const deltaX = touch.clientX - touchStartX.current
    const deltaY = touch.clientY - touchStartY.current

    touchStartX.current = null
    touchStartY.current = null

    const minSwipeDistance = 50
    const maxVerticalDistance = 100

    if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaY) > maxVerticalDistance) {
      return
    }

    if (deltaX > 0) {
      startSwipeAnimation('previous')
    } else {
      startSwipeAnimation('next')
    }
  }

  useEffect(() => {
    if (previousIntervalKey.current === intervalKey) return
    previousIntervalKey.current = intervalKey

    if (!pendingSwipe.current) return

    const { direction } = pendingSwipe.current
    setTransitionEnabled(false)
    setTranslateX(direction === 'next' ? '100%' : '-100%')

    const enterAnimation = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setTransitionEnabled(true)
        setTranslateX('0%')
      })
    })

    const finalize = window.setTimeout(() => {
      pendingSwipe.current = null
      setIsAnimating(false)
    }, slideDurationMs)

    return () => {
      window.cancelAnimationFrame(enterAnimation)
      window.clearTimeout(finalize)
    }
  }, [intervalKey, slideDurationMs])

  return (
    <div>
      {!trainData || trainData.state === 'loading' ? (
        <LoadingIndicator size="normal" padded={true} />
      ) : trainData?.state === 'error' ? (
        <div>{t('wagons.error')}</div>
      ) : (
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
          <StationInterval />
          <div
            style={{
              transform: `translateX(${translateX})`,
              transition: transitionEnabled ? `transform ${slideDurationMs}ms ease` : 'none',
              willChange: 'transform',
            }}
          >
            <WagonRows />
          </div>
          <BottomNavbar />
        </div>
      )}
    </div>
  )
}

export default WagonSection
